import React from 'react';
import {
  Route,
  Navigate,
  createBrowserRouter,
  createRoutesFromElements,
  LoaderFunctionArgs,
  RouterProvider,
} from 'react-router-dom';
import { useApiClient } from '@marageti/z4-lib';
import Login from '../pages/login';
import ResetSendLink from '../pages/reset/send-link';
import ResetSetPassword from '../pages/reset/set-password';
import Activate from '../pages/activate';
import Verification from '../pages/verification';
import Home from '../pages/home';
import Review from '../pages/review';
import ReviewDetail from '../pages/review/detail';
import Logout from '../pages/logout';
import NotFound from '../pages/404';
import Dummy from '../pages/dummy';
import ProtectedLayout from './wrappers/layout-wrapper';
import ProtectedRoutes from './wrappers/protected-wrapper';
import ProtectedStore from './wrappers/store-wrapper';
import { useAuthStore } from '../store';
import ArchivedTrips from '../pages/archived-trips';
import useHotjar from '../hooks/use-hotjar';
import useTripRequestsRoutes from './trip-requests';
import useTripMatchesRoutes from './trip-matches';
import RedirectNavigator from './wrappers/redirect-wrapper';
import Account from '../pages/account';
import MyProfile from '../pages/account/my-profile';
import EditProfile from '../pages/account/edit-profile';
import Security from '../pages/account/security';
import TripRequestConfirmation from '../pages/trip-request-confirmation/logged-in-user';
import NewUserConfirmation from '../pages/trip-request-confirmation/pending-user';
import ErrorBoundaryRouter from '../components/error-boundary/ErrorBoundaryRouter';
import D7TripRedirectWrapper from './wrappers/d7-trip-redirect-wrapper';

const Router = () => {
  const { isAuthenticated } = useAuthStore();
  const apiClient = useApiClient();
  const tripRequestsRoutes = useTripRequestsRoutes();
  const tripMatchesRoutes = useTripMatchesRoutes();
  // const fullPageRoutes = useFullPageRoutes();
  useHotjar();

  const tokenExistsLoader = async ({ request }: LoaderFunctionArgs): Promise<boolean> => {
    const { searchParams } = new URL(request.url);
    const email = searchParams.get('email') || '';
    const token = searchParams.get('token') || '';
    if (email && token) {
      return apiClient.authClient.resetTokenExists(email, token).catch(() => false);
    }
    return false;
  };

  const router = createBrowserRouter(
    createRoutesFromElements(
      <Route element={<RedirectNavigator />} ErrorBoundary={ErrorBoundaryRouter}>
        <Route
          path="/login/*"
          element={isAuthenticated ? <Navigate to={`/${window.location.search}`} /> : <Login />}
        />
        <Route path="logout" element={<Logout />} />
        <Route path="/reset/password" element={<ResetSetPassword />} loader={tokenExistsLoader} />
        <Route path="/reset" element={<ResetSendLink />}>
          <Route index element={null} />
          <Route path="send-email" element={null} />
        </Route>
        <Route path="/activate" element={<Activate />} loader={tokenExistsLoader} />
        <Route path="/verify" element={<Verification />} />
        {/* New user trip request confirmation page */}
        <Route path="/new-account-confirmation" element={<NewUserConfirmation />} />
        <Route path="/" element={<ProtectedRoutes />}>
          <Route element={<D7TripRedirectWrapper />}>
            {/* Logged in user trip request confirmation page */}
            <Route path="/trip-request-confirmation" element={<TripRequestConfirmation />} />
            <Route element={<ProtectedStore />}>
              <Route element={<ProtectedLayout />}>
                <Route index element={<Home />} />
                <Route path="archived-trips" element={<ArchivedTrips />} />
                <Route path="trip-requests" element={<Home />} />
                <Route path="reviews/:reviewId" element={<Review />}>
                  <Route index element={<ReviewDetail />} />
                </Route>
                <Route path="dummy" element={<Dummy />} />
                <Route path="/account" element={<Account />}>
                  <Route index element={<MyProfile />} />
                  <Route path="edit-profile" element={<EditProfile />} />
                  <Route path="security" element={<Security />} />
                </Route>
              </Route>
              <Route element={<ProtectedLayout hidefooter />}>
                <Route path="*" element={<NotFound />} />
                {tripRequestsRoutes}
                {tripMatchesRoutes}
              </Route>
            </Route>
          </Route>
        </Route>
      </Route>,
    ),
  );

  return <RouterProvider router={router} />;
};

export default Router;
